@let cartData = cart$ | async;
@let bounds = bounds$ | async;
@let includedBenefits = includedBenefits$ | async;
@let unpaidServices = unpaidServices$ | async;
@let selectedPassengerId = selectedPassengerId$ | async;
@let selectedPassengerWithInfant = selectedPassengerWithInfant$ | async;
@let pendingServices = pendingServices$ | async;
@let passengerCount = selectedPassengerId ? (selectedPassengerWithInfant ? 2 : 1) : cartData?.passengers?.length;
@let showCtaButton = isInsideModal() && showModalCtaButton();

@if (cartData && bounds) {
  <div
    data-testid="shopping-cart-component"
    class="cart fill nordic-blue-900-text flex flex--column"
    [ngClass]="{
      'rounded-large overflow-hidden border': !isInsideModal(),
      'ps-small-b': !showCtaButton,
      grow: showCtaButton,
    }"
  >
    <div
      [ngClass]="{
        'sticky top z1': isInsideModal(),
      }"
    >
      <section
        class="ps-small"
        [ngClass]="{
          'nordic-blue-25-bg': !isInsideModal(),
          'nordic-blue-900-bg white-text rounded-top-large no-rounding-on-phone': isInsideModal(),
        }"
      >
        <h2 class="font-heading-4 flex flex--space-between flex--middle">
          <span>{{ 'shoppingCart.title' | finLocalization }}</span>
          @if (!isInsideModal()) {
            <span data-testid="shopping-cart-total">{{ cartTotal$ | async | finPrice }}</span>
          } @else {
            <fcom-icon-button
              data-testid="close-shopping-cart"
              [icon]="{ name: SvgLibraryIcon.CLOSE_DELETE }"
              [aria]="{
                label: 'actions.close' | finLocalization,
              }"
              [size]="IconButtonSize.LARGE"
              (clickBtn)="closeShoppingCart()"
            ></fcom-icon-button>
          }
        </h2>
      </section>
      @if (isInsideModal()) {
        <section class="nordic-blue-25-bg ps-small" data-testid="cart-total-price">
          <div class="flex flex--space-between flex--middle">
            <span class="font-body-1">
              {{ 'shoppingCart.totalPriceInfo' | finPluralLocalization: cartData.passengers?.length ?? 1 }}
            </span>
            <span class="font-heading-4">{{ cartTotal$ | async | finPrice }}</span>
          </div>
        </section>
      }
      <div [ngClass]="{ 'elevation-level-1 nordic-blue-25-bg': isInsideModal() }">
        <fin-shopping-cart-passengers
          [passengers]="cartData.passengers"
          [selectedPassengerId]="selectedPassengerId"
          (passengerSelected)="selectPassenger($event)"
          (tabsVisible)="showPassengerTabs.set($event)"
        ></fin-shopping-cart-passengers>
      </div>
    </div>
    <div
      class="flex-1 scroll"
      data-testid="shopping-cart-content"
      [attr.role]="showPassengerTabs() ? 'tabpanel' : null"
      [attr.id]="showPassengerTabs() ? 'sc-pax-panel' : null"
      [attr.tabindex]="showPassengerTabs() ? 0 : null"
    >
      @for (bound of bounds; track bound.id; let i = $index) {
        <section class="ps-small-x ps-small-t" [attr.data-testid]="'bound-section-' + bound.id">
          <div class="flex flex--space-between">
            <h3 class="font-heading-5">
              {{ bound.departure.cityName }}
              <fcom-icon [name]="SvgLibraryIcon.AIRCRAFT_HORIZONTAL"></fcom-icon>
              {{ bound.arrival.cityName }}
            </h3>
            <fcom-icon-button
              [icon]="{ name: SvgLibraryIcon.INFO }"
              [size]="IconButtonSize.SMALL"
              [aria]="{
                label: 'shoppingCart.viewAllFlightDetailsAria' | finLocalization,
              }"
              (clickBtn)="openItineraryDetails(bound, bounds, i, cartData.locations)"
            ></fcom-icon-button>
          </div>
          <span class="font-body-1">{{ bound.departure.dateTime | finDateFormat: 'FULL_NO_YEAR' }}</span>
          <span class="font-body-1 bold-type">
            {{ bound.departure.dateTime | finTime }} - {{ bound.arrival.dateTime | finTime }}
          </span>
          <div class="flex">
            @if (bound.tailNames.length) {
              <div class="flex--nogrow flex ms-xxsmall-b" aria-hidden="true">
                @for (tail of bound.tailNames; track tail; let first = $first) {
                  <div class="flex--nogrow tail {{ tail }}" [class.minus-margin]="!first"></div>
                }
              </div>
            }
            @if (bound.uniqueAirlineNames.length) {
              <div class="flex--nogrow overflow-hidden font-body-2">
                <span class="is-vishidden"> {{ 'bookingSummary.operatedBy' | finLocalization }} </span>
                {{ bound.uniqueAirlineNames.join(', ') }}
              </div>
            }
          </div>
          <div class="font-body-1 ms-xsmall-y flex flex--middle" data-testid="fare-family-info">
            <span class="bold-type nowrap">{{ passengerCount }} x</span>
            <fcom-link
              class="ms-xxsmall-l nowrap overflow-hidden"
              data-testid="fare-family-link"
              [aria]="{ label: 'shoppingCart.viewAllTicketDetailsAria' | finLocalization }"
              [truncate]="true"
              (beforeNavigation)="openFareFamilyDetails(cartData.benefits[bound.fareFamily.code])"
              >{{ bound.fareFamily?.name }}</fcom-link
            >
            <fcom-icon [name]="SvgLibraryIcon.RESERVATION" class="icon-medium ms-xsmall-l flex--shrink0"></fcom-icon>

            @if (offerBoundsPrices()[i]) {
              <div class="flex-1 flex flex--end ms-small-l nowrap">
                <span class="font-body-1" [attr.data-test-id]="'bound-total-price-' + i">
                  {{ offerBoundsPrices()[i] | finPrice }}
                </span>
              </div>
            }
          </div>
          <div class="ticket-benefits font-body-1 ps-large-l">
            @for (benefit of includedBenefits[bound.id]; track benefit.id) {
              <div class="flex flex--space-between ps-xsmall-b">
                <span class="truncate overflow-hidden">
                  @if (benefit.title) {
                    @if (benefit.count) {
                      {{ benefit.count + ' x ' }}
                    }
                    {{ benefit.title }}
                  } @else if (benefit.titleKey) {
                    {{ benefit.titleKey | finLocalization: { amount: benefit.count } }}
                  }
                </span>
              </div>
            }
          </div>
          <div class="font-body-1">
            @for (service of unpaidServices[bound.id]; track service.id) {
              <div class="ps-xsmall-b">
                <ng-container *ngTemplateOutlet="serviceTemplate; context: { $implicit: service }"></ng-container>
              </div>
              @if (service.childProducts) {
                @for (child of service.childProducts; track child.id) {
                  <div class="ps-xsmall-b ps-large-l">
                    <ng-container *ngTemplateOutlet="serviceTemplate; context: { $implicit: child }"></ng-container>
                  </div>
                }
              }
            }
            @for (service of pendingServices?.[bound.id]; track service.id) {
              <div class="ps-xsmall-b">
                <ng-container *ngTemplateOutlet="serviceTemplate; context: { $implicit: service }"></ng-container>
              </div>
              @if (service.childProducts) {
                @for (child of service.childProducts; track child.id) {
                  <div class="ps-xsmall-b ps-large-l">
                    <ng-container *ngTemplateOutlet="serviceTemplate; context: { $implicit: child }"></ng-container>
                  </div>
                }
              }
            }
          </div>
          <div class="separator ps-xxsmall-y border-bottom-dashed"></div>
        </section>
      }
      <div class="flex flex--space-between flex--middle ms-small nordic-blue-50-bg rounded-medium ps-small font-body-1">
        <span>
          {{
            'shoppingCart.totalPriceInfo'
              | finPluralLocalization: (selectedPassengerId ? 1 : (cartData.passengers?.length ?? 1))
          }}
        </span>
        <span data-testid="shopping-cart-total-per-passenger" class="font-heading-4">{{
          cartTotalPricePerPassenger$ | async | finPrice
        }}</span>
      </div>
    </div>
    @if (showCtaButton) {
      <div class="nordic-blue-900-bg flex--nogrow ps-small flex flex--center">
        <fcom-button
          data-testid="shopping-cart-modal-cta-button"
          (clickBtn)="handleModalCtaButtonClick()"
          [theme]="ButtonTheme.LIGHT_PRIMARY_CTA_HEATHER"
          [text]="modalCtaButtonText()"
        />
      </div>
    }
  </div>
}

<fcom-modal
  [title]="'shoppingCart.fareFamilyModalTitle' | finLocalization"
  [buttons]="ModalButtons.DONE"
  [(open)]="fareFamilyModalData.isOpen"
>
  <fin-fare-family-details class="block pr-medium" [fareFamilies]="[fareFamilyModalData.fareFamily]" />
</fcom-modal>

<fcom-modal
  [title]="'shoppingCart.itineraryModalTitle' | finLocalization"
  [buttons]="ModalButtons.DONE"
  [(open)]="itineraryModalData.isOpen"
>
  <fin-itinerary-timeline
    class="block pr-medium"
    [locations]="itineraryModalData.locations"
    [bound]="itineraryModalData.bound"
    [boundType]="itineraryModalData.boundType"
    [isBookingFlow]="true"
  />
</fcom-modal>

<ng-template #serviceTemplate let-service>
  <div class="flex">
    <span class="nowrap ms-xxsmall-r">{{ service.count }} x</span>
    @if (service.icon) {
      <fcom-icon [name]="service.icon" class="ms-xxsmall-r flex--shrink0"></fcom-icon>
    }
    <span class="flex-1 truncate overflow-hidden">
      @if (service.title) {
        {{ service.title }}
      } @else if (service.titleKey) {
        {{ service.titleKey | finLocalization: service.titleParams }}
      }
    </span>
    @if (service.price) {
      <span class="nowrap ps-small-l">{{ service.price | finPrice }}</span>
    }
  </div>
</ng-template>
